import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../firebase';
import { doc, getDoc, collection, getDocs, query, orderBy, where, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Html5QrcodeScanner } from 'html5-qrcode';
import Barcode from 'react-barcode';
import randomInt from 'random-int'; 

const OrderScanner = () => {
  const [scanning, setScanning] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [callNotes, setCallNotes] = useState([]);
  const [pathaoStatus, setPathaoStatus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualOrderId, setManualOrderId] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [returnOrders, setReturnOrders] = useState([]);
  const [returnStats, setReturnStats] = useState({ total: 0, received: 0, pending: 0 });
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnNote, setReturnNote] = useState('');
  const [currentView, setCurrentView] = useState('scanner'); // 'scanner' or 'returns'
  const scannerRef = useRef(null);
  const html5QrcodeScannerRef = useRef(null);

  // Toast notification state
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success', 'error', 'warning', 'info'
  const [showToast, setShowToast] = useState(false);

  // Return RCV states
  const [showReturnRCVDialog, setShowReturnRCVDialog] = useState(false);
  const [returnRCVStep, setReturnRCVStep] = useState('scan'); // 'scan', 'product-select', 'quantity-input', 'basket-select', 'preview'
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [returnProducts, setReturnProducts] = useState([]); // Products to be returned
  const [allProducts, setAllProducts] = useState([]); // All products from DB
  const [selectedProductForReturn, setSelectedProductForReturn] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [manualProductCode, setManualProductCode] = useState('');
  const [returnBaskets, setReturnBaskets] = useState([]);
  const [selectedReturnBasket, setSelectedReturnBasket] = useState(null);
  const [generatedReturnBarcodes, setGeneratedReturnBarcodes] = useState([]);
  const [barcodeScanner, setBarcodeScanner] = useState(null);
  const returnBarcodeScannerRef = useRef(null);

  // Toast notification function
  const showToastNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  useEffect(() => {
    if (scanning && !html5QrcodeScannerRef.current) {
      initializeScanner();
    }

    return () => {
      if (html5QrcodeScannerRef.current) {
        html5QrcodeScannerRef.current.clear().catch(console.error);
        html5QrcodeScannerRef.current = null;
      }
    };
  }, [scanning]);

  useEffect(() => {
    if (currentView === 'returns') {
      fetchReturnOrders();
    }
  }, [currentView]);

  // Auto-search when barcode scanner inputs data
  useEffect(() => {
    if (manualOrderId && manualOrderId.trim().length > 0 && !loading && !orderData) {
      const timeoutId = setTimeout(() => {
        const input = manualOrderId.trim();
        const firstHyphenIndex = input.indexOf('-');
        const orderId = firstHyphenIndex !== -1 ? input.substring(firstHyphenIndex + 1) : input;
        
        fetchOrderDetails(orderId);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [manualOrderId]);

  const fetchReturnOrders = async () => {
    try {
      setLoading(true);
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('status', '==', 'Return')
      );
      
      const querySnapshot = await getDocs(q);
      const orders = [];
      
      querySnapshot.forEach((doc) => {
        orders.push({
          id: doc.id,
          ...doc.data()
        });
      });

      // Sort by created_at descending
      orders.sort((a, b) => {
        const dateA = a.created_at?.toDate ? a.created_at.toDate() : new Date(a.created_at || 0);
        const dateB = b.created_at?.toDate ? b.created_at.toDate() : new Date(b.created_at || 0);
        return dateB - dateA;
      });

      setReturnOrders(orders);

      // Calculate stats
      const total = orders.length;
      const received = orders.filter(o => o.CourierReturnRCV === true).length;
      const pending = total - received;
      
      setReturnStats({ total, received, pending });
    } catch (error) {
      console.error('Error fetching return orders:', error);
      setError('Failed to fetch return orders: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSearch = async () => {
    if (!searchPhone.trim()) {
      setError('Please enter a phone number');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('customerInfo.phone', '==', searchPhone.trim())
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        setError('No orders found for this phone number');
        setLoading(false);
        return;
      }

      // Get the first order (or you can show a list if multiple)
      const firstDoc = querySnapshot.docs[0];
      fetchOrderDetails(firstDoc.id);
      
    } catch (error) {
      console.error('Error searching by phone:', error);
      setError('Failed to search: ' + error.message);
      setLoading(false);
    }
  };

  const handleReturnRCV = async () => {
    if (!orderData) return;

    const user = auth.currentUser;
    const userEmail = user?.email || 'Unknown';

    try {
      setLoading(true);
      const orderRef = doc(db, 'orders', orderData.id);
      
      await updateDoc(orderRef, {
        ThisProductIsCourier: false,
        CourierReturnRCV: true,
        returnRcvNote: returnNote.trim() || null,
        returnedRcvBy: userEmail,
        returnRcvAt: new Date()
      });

      setShowReturnDialog(false);
      setReturnNote('');
      showToastNotification('পার্সেল সফলভাবে গ্রহণ করা হয়েছে!', 'success');
      
      // Refresh order data
      fetchOrderDetails(orderData.id);
      
    } catch (error) {
      console.error('Error updating return RCV:', error);
      showToastNotification('পার্সেল গ্রহণ করতে এরর হয়েছে: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // ==================== NEW RETURN RCV WITH BARCODE FUNCTIONS ====================

  const startReturnRCVProcess = async () => {
    if (!orderData || orderData.status !== 'Return') return;

    // Load all products and return baskets
    await loadAllProducts();
    await loadReturnBaskets();

    // Reset states
    setReturnProducts([]);
    setScannedBarcode('');
    setSelectedProductForReturn(null);
    setProductQuantity(1);
    setManualProductCode('');
    setGeneratedReturnBarcodes([]);
    setSelectedReturnBasket(null);
    
    setReturnRCVStep('scan');
    setShowReturnRCVDialog(true);
  };

  const loadAllProducts = async () => {
    try {
      const productsRef = collection(db, 'products');
      const snapshot = await getDocs(productsRef);
      
      const products = [];
      snapshot.forEach((doc) => {
        products.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setAllProducts(products);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadReturnBaskets = async () => {
    try {
      const basketsRef = collection(db, 'stock_basket');
      const q = query(basketsRef, where('basket_type', '==', 'return'), orderBy('created_at', 'desc'));
      const snapshot = await getDocs(q);
      
      const basketsData = [];
      for (const docSnap of snapshot.docs) {
        const basketData = { id: docSnap.id, ...docSnap.data() };
        
        // Calculate fill percentage
        const stockProductsRef = collection(db, 'stock_basket', docSnap.id, 'stock_product');
        const stockSnapshot = await getDocs(stockProductsRef);
        
        let totalVolume = 0;
        stockSnapshot.forEach((stockDoc) => {
          const product = stockDoc.data();
          const volume = (product.width || 0) * (product.height || 0) * (product.length || 0);
          totalVolume += volume;
        });
        
        const basketVolume = basketData.width * basketData.height * basketData.length;
        const fillPercentage = (totalVolume / basketVolume) * 100;
        
        basketData.fillPercentage = fillPercentage;
        basketData.remainingVolume = basketVolume - totalVolume;
        
        basketsData.push(basketData);
      }

      // If no return baskets, load fresh baskets as fallback
      if (basketsData.length === 0) {
        const freshQuery = query(basketsRef, where('basket_type', '==', 'fresh'), orderBy('created_at', 'desc'));
        const freshSnapshot = await getDocs(freshQuery);
        
        for (const docSnap of freshSnapshot.docs) {
          const basketData = { id: docSnap.id, ...docSnap.data() };
          
          const stockProductsRef = collection(db, 'stock_basket', docSnap.id, 'stock_product');
          const stockSnapshot = await getDocs(stockProductsRef);
          
          let totalVolume = 0;
          stockSnapshot.forEach((stockDoc) => {
            const product = stockDoc.data();
            const volume = (product.width || 0) * (product.height || 0) * (product.length || 0);
            totalVolume += volume;
          });
          
          const basketVolume = basketData.width * basketData.height * basketData.length;
          const fillPercentage = (totalVolume / basketVolume) * 100;
          
          basketData.fillPercentage = fillPercentage;
          basketData.remainingVolume = basketVolume - totalVolume;
          basketData.isFreshBasket = true;
          
          basketsData.push(basketData);
        }
      }
      
      // Sort by fill percentage
      basketsData.sort((a, b) => a.fillPercentage - b.fillPercentage);
      
      setReturnBaskets(basketsData);
    } catch (error) {
      console.error('Error loading return baskets:', error);
    }
  };

  const handleBarcodeScan = async (barcode) => {
    setScannedBarcode(barcode);
    
    // Extract first 4 digits as product code
    const productCode = barcode.substring(0, 4);
    
    // Search for product with this code
    const product = allProducts.find(p => p.product_code === productCode);
    
    if (product) {
      const cartItem = orderData?.cartItems?.find(item => item.productId === product.id);
      const itemQuantity = cartItem?.quantity || 1;
      
      setSelectedProductForReturn(product);
      setProductQuantity(itemQuantity);
      setReturnRCVStep('quantity-input');
    } else {
      showToastNotification(`Product code ${productCode} not found! Please select manually.`, 'warning');
      setReturnRCVStep('product-select');
    }
  };

  const handleManualProductSelect = (product) => {
    setSelectedProductForReturn(product);
    
    const cartItem = orderData?.cartItems?.find(item => item.productId === product.id);
    const itemQuantity = cartItem?.quantity || 1;
    setProductQuantity(itemQuantity);
    
    if (!product.product_code) {
      setManualProductCode('');
    } else {
      setReturnRCVStep('quantity-input');
    }
  };

  const handleSaveProductCode = async () => {
    if (!manualProductCode || manualProductCode.length !== 4 || !/^\d+$/.test(manualProductCode)) {
      showToastNotification('Product code must be exactly 4 digits', 'error');
      return;
    }

    const existingProduct = allProducts.find(p => p.product_code === manualProductCode);
    if (existingProduct && existingProduct.id !== selectedProductForReturn.id) {
      showToastNotification('This product code is already used by another product!', 'error');
      return;
    }

    try {
      const productRef = doc(db, 'products', selectedProductForReturn.id);
      await updateDoc(productRef, {
        product_code: manualProductCode
      });

      selectedProductForReturn.product_code = manualProductCode;
      
      const updatedProducts = allProducts.map(p => 
        p.id === selectedProductForReturn.id ? {...p, product_code: manualProductCode} : p
      );
      setAllProducts(updatedProducts);

      setReturnRCVStep('quantity-input');
    } catch (error) {
      showToastNotification('Error saving product code: ' + error.message, 'error');
    }
  };

  const handleQuantityConfirm = () => {
    if (!productQuantity || productQuantity < 1) {
      showToastNotification('Please enter a valid quantity (minimum 1)', 'error');
      return;
    }
    
    setReturnRCVStep('basket-select');
  };

  const handleBasketSelect = (basket) => {
    setSelectedReturnBasket(basket);
  };

  const generateReturnBarcode = (productCode, basketNumber, serial) => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);
    const dateStr = `${day}${month}${year}`;
    return `${productCode}0${dateStr}0${basketNumber}0${serial}`;
  };

  const handleAddProductToReturn = async () => {
    if (!selectedProductForReturn || !selectedReturnBasket || !productQuantity || productQuantity < 1) {
      showToastNotification('Please select product, basket, and valid quantity', 'error');
      return;
    }

    const now = new Date();
    const newProducts = [];

    for (let i = 0; i < productQuantity; i++) {
      const serial = randomInt(1, 9999);
      const newBarcode = generateReturnBarcode(
        selectedProductForReturn.product_code,
        selectedReturnBasket.basket_number,
        serial
      );

      const returnProduct = {
        productId: selectedProductForReturn.id,
        productName: selectedProductForReturn.name,
        productCode: selectedProductForReturn.product_code,
        image: selectedProductForReturn.imageUrl || selectedProductForReturn.image,
        width: selectedProductForReturn.width || 0,
        height: selectedProductForReturn.height || 0,
        length: selectedProductForReturn.length || 0,
        oldBarcode: scannedBarcode,
        newBarcode: newBarcode,
        basketNumber: selectedReturnBasket.basket_number,
        basketId: selectedReturnBasket.id,
        serial: serial,
        addedAt: now,
        quantityIndex: i + 1,
        totalQuantity: productQuantity
      };

      newProducts.push(returnProduct);
    }

    setReturnProducts([...returnProducts, ...newProducts]);
    
    setScannedBarcode('');
    setSelectedProductForReturn(null);
    setProductQuantity(1);
    setReturnRCVStep('scan');
    
    showToastNotification(`${productQuantity} item(s) added! Scan next product or complete the process.`, 'success');
  };

  const completeReturnRCV = async () => {
    if (returnProducts.length === 0) {
      showToastNotification('No products added!', 'error');
      return;
    }

    const user = auth.currentUser;
    const userEmail = user?.email || 'Unknown';

    try {
      setLoading(true);

      for (const product of returnProducts) {
        const now = new Date();
        const stockProductDocId = `return_${orderData.id}_${product.serial}_${now.getTime()}`;
        const stockProductRef = doc(db, 'stock_basket', product.basketId, 'stock_product', stockProductDocId);
        
        await setDoc(stockProductRef, {
          product_id: product.productId,
          product_name: product.productName,
          product_code: product.productCode,
          barcode: product.newBarcode,
          old_barcode: product.oldBarcode,
          image: product.image || '',
          width: product.width,
          height: product.height,
          length: product.length,
          basket_number: product.basketNumber,
          is_return: true,
          order_id: orderData.id,
          return_rcv_note: returnNote.trim() || null,
          returned_rcv_by: userEmail,
          quantity_index: product.quantityIndex,
          total_quantity: product.totalQuantity,
          created_at: serverTimestamp(),
          created_datetime: now.toLocaleString('en-GB')
        });
      }

      const orderRef = doc(db, 'orders', orderData.id);
      await updateDoc(orderRef, {
        ThisProductIsCourier: false,
        CourierReturnRCV: true,
        returnRcvNote: returnNote.trim() || null,
        returnedRcvBy: userEmail,
        returnRcvAt: new Date(),
        returnProcessedProducts: returnProducts.length
      });

      setGeneratedReturnBarcodes(returnProducts);
      setReturnRCVStep('preview');

      showToastNotification(`✓ Return received successfully! ${returnProducts.length} products processed.`, 'success');
      
      await fetchOrderDetails(orderData.id);

    } catch (error) {
      console.error('Error completing return RCV:', error);
      showToastNotification('Error: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const printReturnLabel = (item) => {
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Return Label - ${item.productName}</title>
        <script src="https://unpkg.com/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: #f0f0f0;
            padding: 10px;
          }
          
          .barcode-label {
            width: 87.6mm;
            height: 101.6mm;
            background: white;
            padding: 3mm;
            box-shadow: 0 0 10px rgba(0,0,0,0.2);
            border: 2px solid #000;
            position: relative;
            display: flex;
            flex-direction: column;
          }
          
          .label-header {
            text-align: center;
            margin-bottom: 3mm;
            border-bottom: 2px solid #000;
            padding-bottom: 2mm;
          }
          
          .label-title {
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 1mm;
          }
          
          .label-subtitle {
            font-size: 11px;
            margin-top: 1mm;
            font-weight: bold;
          }
          
          .return-badge {
            border: 2px solid #000;
            padding: 2mm;
            text-align: center;
            font-weight: bold;
            font-size: 12px;
            margin-bottom: 2mm;
          }
          
          .label-divider {
            border: none;
            border-top: 1px solid #000;
            margin: 2mm 0;
          }
          
          .product-name {
            font-size: 14px;
            margin-bottom: 2mm;
            min-height: 10mm;
            line-height: 1.3;
            font-weight: 500;
          }
          
          .buscat-box {
            font-size: 15px;
            font-weight: bold;
            margin-bottom: 2mm;
            padding: 2mm;
            border: 2px solid #000;
            text-align: center;
          }
          
          .return-info {
            font-size: 10px;
            padding: 2mm;
            border: 1px dashed #000;
            margin-bottom: 2mm;
          }
          
          .return-info-row {
            margin: 1mm 0;
          }
          
          .quantity-badge {
            border: 1px solid #000;
            padding: 1mm 2mm;
            font-size: 10px;
            font-weight: bold;
            display: inline-block;
            margin-top: 1mm;
          }
          
          .barcode-container {
            text-align: center;
            margin: 3mm 0;
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
          }
          
          #barcode {
            margin: 0 auto;
          }
          
          .barcode-text {
            font-size: 13px;
            margin-top: 2mm;
            font-family: monospace;
            font-weight: bold;
          }
          
          .old-barcode {
            font-size: 9px;
            color: #333;
            margin-top: 1mm;
          }
          
          .contact-info {
            text-align: center;
            font-size: 9px;
            margin-top: 2mm;
            padding-top: 2mm;
            border-top: 1px solid #000;
          }
          
          .contact-line {
            margin: 0.5mm 0;
          }
          
          .label-footer {
            position: absolute;
            bottom: 2mm;
            left: 3mm;
            right: 3mm;
            font-size: 8px;
            display: flex;
            justify-content: space-between;
          }
          
          .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            background: #000;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 16px;
            font-weight: bold;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
            z-index: 1000;
          }
          
          .print-button:hover {
            background: #333;
          }
          
          @media print {
            body {
              background: white !important;
              padding: 0;
            }
            
            .barcode-label {
              box-shadow: none;
            }
            
            .print-button {
              display: none !important;
            }
          }
          
          @page {
            size: 87.6mm 101.6mm;
            margin: 0;
          }
        </style>
      </head>
      <body>
        <button class="print-button" onclick="window.print()">🖨️ Print Label</button>
        
        <div class="barcode-label">
          <!-- Header -->
          <div class="label-header">
            <div class="label-title">Elevate Mart</div>
            <div class="label-subtitle">R STOCK LABEL</div>
          </div>

          <!-- Return Badge -->
          <div class="return-badge">
            R ITEM ${item.totalQuantity > 1 ? `(${item.quantityIndex}/${item.totalQuantity})` : ''}
          </div>

          <!-- Product Name -->
          <div class="product-name">
            ${item.productName.length > 70 ? item.productName.substring(0, 70) + '...' : item.productName}
            ${item.totalQuantity > 1 ? `<div class="quantity-badge">Item ${item.quantityIndex} of ${item.totalQuantity}</div>` : ''}
          </div>

          <!-- Basket Number -->
          <div class="buscat-box">
            R Basket: ${item.basketNumber}
          </div>

          <!-- Return Info -->
          <div class="return-info">
            <div class="return-info-row"><strong>Received By:</strong> ${item.receivedBy || 'N/A'}</div>
            <div class="return-info-row"><strong>Order ID:</strong> ${item.orderId || 'N/A'}</div>
            ${item.returnNote ? `<div class="return-info-row"><strong>Note:</strong> ${item.returnNote}</div>` : ''}
          </div>

          <hr class="label-divider" />

          <!-- Barcode -->
          <div class="barcode-container">
            <svg id="barcode"></svg>
            <div class="barcode-text">${item.newBarcode}</div>
            ${item.oldBarcode ? `<div class="old-barcode">Old: ${item.oldBarcode}</div>` : ''}
          </div>

          <!-- Contact Info -->
          <div class="contact-info">
            <div class="contact-line">www.elevatemart.com.bd</div>
            <div class="contact-line">Call: +8809647001250</div>
          </div>
        </div>
        
        <script>
          JsBarcode("#barcode", "${item.newBarcode}", {
            format: "CODE128",
            width: 1.8,
            height: 50,
            displayValue: false,
            margin: 0,
            background: "#ffffff"
          });
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const printAllReturnLabels = () => {
    generatedReturnBarcodes.forEach((item, index) => {
      setTimeout(() => {
        printReturnLabel({
          ...item,
          receivedBy: auth.currentUser?.email || 'Unknown',
          orderId: orderData?.orderId || orderData?.id,
          returnNote: returnNote
        });
      }, index * 300);
    });
  };

  const closeReturnRCVDialog = () => {
    setShowReturnRCVDialog(false);
    setReturnRCVStep('scan');
    setReturnProducts([]);
    setGeneratedReturnBarcodes([]);
    setScannedBarcode('');
    setSelectedProductForReturn(null);
    setProductQuantity(1);
    setSelectedReturnBasket(null);
    setReturnNote('');
  };

  // ==================== END RETURN RCV FUNCTIONS ====================

  const initializeScanner = () => {
    const config = {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      aspectRatio: 1.0,
    };

    html5QrcodeScannerRef.current = new Html5QrcodeScanner(
      "qr-reader",
      config,
      false
    );

    html5QrcodeScannerRef.current.render(onScanSuccess, onScanError);
  };

  const onScanSuccess = (decodedText) => {
    console.log("QR Code scanned:", decodedText);
    
    if (html5QrcodeScannerRef.current) {
      html5QrcodeScannerRef.current.clear();
      html5QrcodeScannerRef.current = null;
    }
    
    setScanning(false);
    
    const firstHyphenIndex = decodedText.indexOf('-');
    const orderId = firstHyphenIndex !== -1 ? decodedText.substring(firstHyphenIndex + 1) : decodedText;
    
    fetchOrderDetails(orderId);
  };

  const onScanError = (error) => {
    // Ignore scan errors as they happen frequently
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      setLoading(true);
      setError('');
      setOrderData(null);
      setCallNotes([]);
      setPathaoStatus([]);

      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);

      if (!orderSnap.exists()) {
        setError('Order not found!');
        setLoading(false);
        return;
      }

      const order = {
        id: orderSnap.id,
        ...orderSnap.data()
      };

      setOrderData(order);

      // Fetch call notes
      const callNotesRef = collection(db, 'orders', orderId, 'callNotes');
      const callNotesQuery = query(callNotesRef, orderBy('timestamp', 'desc'));
      const callNotesSnap = await getDocs(callNotesQuery);
      
      const notes = [];
      callNotesSnap.forEach((doc) => {
        notes.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setCallNotes(notes);

      // Fetch pathao status
      const pathaoStatusRef = collection(db, 'orders', orderId, 'pathaostatus');
      const pathaoStatusQuery = query(pathaoStatusRef, orderBy('updated_at', 'desc'));
      const pathaoStatusSnap = await getDocs(pathaoStatusQuery);
      
      const statuses = [];
      pathaoStatusSnap.forEach((doc) => {
        statuses.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setPathaoStatus(statuses);

    } catch (error) {
      console.error('Error fetching order:', error);
      setError('Failed to fetch order details: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSearch = () => {
    if (!manualOrderId.trim()) {
      setError('Please enter an Order ID');
      return;
    }
    
    const input = manualOrderId.trim();
    const firstHyphenIndex = input.indexOf('-');
    const orderId = firstHyphenIndex !== -1 ? input.substring(firstHyphenIndex + 1) : input;
    
    fetchOrderDetails(orderId);
  };

  const handleStartScanning = () => {
    setScanning(true);
    setError('');
    setOrderData(null);
  };

  const handleStopScanning = () => {
    if (html5QrcodeScannerRef.current) {
      html5QrcodeScannerRef.current.clear();
      html5QrcodeScannerRef.current = null;
    }
    setScanning(false);
  };

  const handleReset = () => {
    setOrderData(null);
    setCallNotes([]);
    setPathaoStatus([]);
    setError('');
    setManualOrderId('');
    setSearchPhone('');
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes === 1) return '1 minute ago';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInHours === 1) return '1 hour ago';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInDays === 1) return '1 day ago';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInWeeks === 1) return '1 week ago';
    if (diffInWeeks < 4) return `${diffInWeeks} weeks ago`;
    if (diffInMonths === 1) return '1 month ago';
    if (diffInMonths < 12) return `${diffInMonths} months ago`;
    if (diffInYears === 1) return '1 year ago';
    return `${diffInYears} years ago`;
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    
    if (statusLower.includes('delivered') || statusLower.includes('completed')) {
      return 'bg-green-100 text-green-800 border-green-300';
    } else if (statusLower.includes('hold') || statusLower.includes('pending')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    } else if (statusLower.includes('cancelled') || statusLower.includes('failed')) {
      return 'bg-red-100 text-red-800 border-red-300';
    } else if (statusLower.includes('return')) {
      return 'bg-orange-100 text-orange-800 border-orange-300';
    } else if (statusLower.includes('transit') || statusLower.includes('picked')) {
      return 'bg-blue-100 text-blue-800 border-blue-300';
    } else {
      return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Toast Notification Component
  const ToastNotification = () => {
    if (!showToast) return null;

    const getToastColor = () => {
      switch (toastType) {
        case 'success':
          return 'bg-green-500';
        case 'error':
          return 'bg-red-500';
        case 'warning':
          return 'bg-yellow-500';
        case 'info':
          return 'bg-blue-500';
        default:
          return 'bg-gray-500';
      }
    };

    const getToastIcon = () => {
      switch (toastType) {
        case 'success':
          return '✓';
        case 'error':
          return '✕';
        case 'warning':
          return '⚠';
        case 'info':
          return 'ℹ';
        default:
          return '●';
      }
    };

    return (
      <div className="fixed top-4 right-4 z-[9999] animate-slideIn">
        <div className={`${getToastColor()} text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 min-w-[300px] max-w-md`}>
          <div className="text-2xl font-bold">{getToastIcon()}</div>
          <div className="flex-1 font-medium">{toastMessage}</div>
          <button
            onClick={() => setShowToast(false)}
            className="text-white hover:text-gray-200 text-xl font-bold ml-2"
          >
            ✕
          </button>
        </div>
      </div>
    );
  };

  // Return RCV Dialog Component
  const ReturnRCVDialog = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="p-6 border-b bg-orange-50 sticky top-0 z-10">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold text-orange-800">Return RCV Process</h3>
              <p className="text-sm text-orange-600 mt-1">
                Order: {orderData?.orderId || orderData?.id} | Products Added: {returnProducts.length}
              </p>
            </div>
            <button
              onClick={closeReturnRCVDialog}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step: Scan Barcode */}
          {returnRCVStep === 'scan' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6 text-center">
                <h4 className="text-xl font-bold text-blue-800 mb-3">📱 Scan Product Barcode</h4>
                <p className="text-blue-600 mb-4">
                  Scan the existing product barcode or enter it manually
                </p>
                
                <div className="max-w-md mx-auto">
                  <input
                    type="text"
                    value={scannedBarcode}
                    onChange={(e) => setScannedBarcode(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && scannedBarcode.trim()) {
                        handleBarcodeScan(scannedBarcode.trim());
                      }
                    }}
                    placeholder="Scan or enter barcode..."
                    className="w-full px-4 py-3 border-2 border-blue-300 rounded-lg text-center text-lg font-mono focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                  
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={() => scannedBarcode.trim() && handleBarcodeScan(scannedBarcode.trim())}
                      disabled={!scannedBarcode.trim()}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
                    >
                      Process Barcode
                    </button>
                    <button
                      onClick={() => setReturnRCVStep('product-select')}
                      className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 rounded-lg"
                    >
                      Manual Product Select
                    </button>
                  </div>
                </div>
              </div>

              {/* Show added products */}
              {returnProducts.length > 0 && (
                <div className="border-2 border-green-300 rounded-lg p-4 bg-green-50">
                  <h5 className="font-bold text-green-800 mb-3">✓ Products Added ({returnProducts.length})</h5>
                  <div className="space-y-2">
                    {returnProducts.map((product, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-white p-3 rounded-lg">
                        <img 
                          src={product.image || 'https://via.placeholder.com/50'} 
                          alt={product.productName}
                          className="w-12 h-12 object-cover rounded"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-sm">{product.productName}</div>
                          <div className="text-xs text-gray-600">New Barcode: {product.newBarcode}</div>
                          {product.totalQuantity > 1 && (
                            <div className="text-xs text-green-600 font-semibold mt-1">
                              Item {product.quantityIndex} of {product.totalQuantity}
                            </div>
                          )}
                        </div>
                        <div className="text-xs bg-orange-100 px-2 py-1 rounded">
                          Basket #{product.basketNumber}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={completeReturnRCV}
                    disabled={loading}
                    className="w-full mt-4 bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-lg disabled:opacity-50"
                  >
                    {loading ? 'Processing...' : '✓ Complete Return RCV'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step: Product Selection */}
          {returnRCVStep === 'product-select' && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                <h4 className="text-lg font-bold text-yellow-800 mb-2">⚠️ Product Not Found</h4>
                <p className="text-yellow-700">Please select the product manually from the list below:</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {allProducts.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => handleManualProductSelect(product)}
                    className="border-2 border-gray-300 rounded-lg p-3 cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition"
                  >
                    <img
                      src={product.imageUrl || product.image || 'https://via.placeholder.com/100'}
                      alt={product.name}
                      className="w-full h-32 object-cover rounded mb-2"
                    />
                    <div className="text-sm font-semibold">{product.name}</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Code: {product.product_code || 'Not Set'}
                    </div>
                  </div>
                ))}
              </div>

              {selectedProductForReturn && !selectedProductForReturn.product_code && (
                <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
                  <h5 className="font-bold text-orange-800 mb-3">Set Product Code</h5>
                  <p className="text-sm text-orange-700 mb-3">
                    This product doesn't have a code. Please enter a unique 4-digit code:
                  </p>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={manualProductCode}
                      onChange={(e) => setManualProductCode(e.target.value)}
                      maxLength={4}
                      placeholder="Enter 4-digit code"
                      className="flex-1 px-4 py-2 border-2 border-orange-300 rounded-lg"
                    />
                    <button
                      onClick={handleSaveProductCode}
                      className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-lg"
                    >
                      Save & Continue
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={() => setReturnRCVStep('scan')}
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg"
              >
                ← Back to Scan
              </button>
            </div>
          )}

          {/* Step: Quantity Input */}
          {returnRCVStep === 'quantity-input' && (
            <div className="space-y-4">
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-6">
                <h4 className="text-xl font-bold text-green-800 mb-4">✓ Product Selected</h4>
                
                {/* Product Info */}
                <div className="flex items-center gap-4 mb-6 bg-white p-4 rounded-lg">
                  <img
                    src={selectedProductForReturn?.imageUrl || selectedProductForReturn?.image || 'https://via.placeholder.com/100'}
                    alt={selectedProductForReturn?.name}
                    className="w-24 h-24 object-cover rounded"
                  />
                  <div className="flex-1">
                    <h5 className="text-lg font-bold text-gray-800">{selectedProductForReturn?.name}</h5>
                    <p className="text-sm text-gray-600">Code: {selectedProductForReturn?.product_code}</p>
                    {(() => {
                      const cartItem = orderData?.cartItems?.find(item => item.productId === selectedProductForReturn?.id);
                      return cartItem ? (
                        <p className="text-sm text-blue-600 font-semibold mt-1">
                          Ordered Quantity: {cartItem.quantity}
                        </p>
                      ) : null;
                    })()}
                  </div>
                </div>

                {/* Quantity Input */}
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                  <label className="block text-lg font-bold text-blue-800 mb-3">
                    📦 Return Quantity
                  </label>
                  <p className="text-sm text-blue-600 mb-4">
                    Quantity auto-filled from order. You can adjust if needed. Each item will get a unique barcode.
                  </p>
                  
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setProductQuantity(Math.max(1, productQuantity - 1))}
                      className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-3 rounded-lg text-xl"
                    >
                      −
                    </button>
                    
                    <input
                      type="number"
                      value={productQuantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 1;
                        setProductQuantity(Math.max(1, val));
                      }}
                      min="1"
                      className="flex-1 px-6 py-3 border-2 border-blue-300 rounded-lg text-center text-2xl font-bold focus:ring-2 focus:ring-blue-500"
                    />
                    
                    <button
                      onClick={() => setProductQuantity(productQuantity + 1)}
                      className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-3 rounded-lg text-xl"
                    >
                      +
                    </button>
                  </div>

                  <div className="mt-4 text-center text-sm text-gray-600">
                    {productQuantity} barcode{productQuantity > 1 ? 's' : ''} will be generated
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setReturnRCVStep('scan');
                    setSelectedProductForReturn(null);
                    setProductQuantity(1);
                  }}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg"
                >
                  ← Back
                </button>
                <button
                  onClick={handleQuantityConfirm}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg"
                >
                  Continue to Basket Selection →
                </button>
              </div>
            </div>
          )}

          {/* Step: Basket Selection */}
          {returnRCVStep === 'basket-select' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                <h4 className="text-lg font-bold text-blue-800 mb-2">📦 Select Return Basket</h4>
                <p className="text-blue-700">Choose a basket to store this return item:</p>
                <div className="mt-2 text-sm">
                  <strong>Product:</strong> {selectedProductForReturn?.name}
                  <br />
                  <strong>Quantity:</strong> {productQuantity} item{productQuantity > 1 ? 's' : ''}
                </div>
              </div>

              {returnBaskets.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-600">No baskets available. Please create a return basket first.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {returnBaskets.map((basket) => (
                    <div
                      key={basket.id}
                      onClick={() => handleBasketSelect(basket)}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition ${
                        selectedReturnBasket?.id === basket.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-blue-300'
                      } ${basket.fillPercentage >= 100 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-bold text-lg">Basket #{basket.basket_number}</div>
                          <div className="text-xs text-gray-600">
                            {basket.basket_type} {basket.isFreshBasket && '(Fallback)'}
                          </div>
                        </div>
                        {selectedReturnBasket?.id === basket.id && (
                          <div className="bg-blue-500 text-white px-2 py-1 rounded text-xs font-bold">
                            Selected
                          </div>
                        )}
                      </div>

                      <div className="text-sm text-gray-600 mb-2">
                        {basket.width}" × {basket.height}" × {basket.length}"
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                        <div 
                          className={`h-2 rounded-full ${
                            basket.fillPercentage >= 100 ? 'bg-red-500' :
                            basket.fillPercentage >= 80 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(basket.fillPercentage, 100)}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-600">
                        {basket.fillPercentage.toFixed(1)}% Full | 
                        Remaining: {basket.remainingVolume.toFixed(2)} cu in
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setReturnRCVStep('quantity-input')}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg"
                >
                  ← Back
                </button>
                <button
                  onClick={handleAddProductToReturn}
                  disabled={!selectedReturnBasket}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-6 rounded-lg disabled:opacity-50"
                >
                  Add {productQuantity} Item{productQuantity > 1 ? 's' : ''} & Continue
                </button>
              </div>
            </div>
          )}

          {/* Step: Preview & Print */}
          {returnRCVStep === 'preview' && (
            <div className="space-y-4">
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 text-center">
                <h4 className="text-xl font-bold text-green-800 mb-2">✓ Return RCV Completed!</h4>
                <p className="text-green-700">
                  {generatedReturnBarcodes.length} products processed successfully
                </p>
              </div>

              {/* Return Note - WITH FIX FOR FOCUS ISSUE */}
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Return Note (Optional)
                </label>
                <textarea
                  value={returnNote}
                  onChange={(e) => {
                    e.stopPropagation();
                    setReturnNote(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  onBlur={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  onKeyPress={(e) => e.stopPropagation()}
                  onKeyUp={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  placeholder="Add any notes about this return..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  rows="3"
                />
              </div>

              {/* Print Labels */}
              <div className="border-2 border-gray-300 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h5 className="font-bold text-lg">Print Return Labels</h5>
                  <button
                    onClick={printAllReturnLabels}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg"
                  >
                    🖨️ Print All Labels
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {generatedReturnBarcodes.map((item, idx) => (
                    <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                      <img
                        src={item.image || 'https://via.placeholder.com/100'}
                        alt={item.productName}
                        className="w-full h-24 object-cover rounded mb-2"
                      />
                      <div className="text-sm font-semibold mb-1">{item.productName.substring(0, 40)}</div>
                      {item.totalQuantity > 1 && (
                        <div className="text-xs text-green-600 font-semibold mb-1">
                          Item {item.quantityIndex}/{item.totalQuantity}
                        </div>
                      )}
                      <div className="text-xs text-gray-600 mb-2">
                        Basket #{item.basketNumber}
                      </div>
                      <div className="flex justify-center mb-2">
                        <Barcode 
                          value={item.newBarcode}
                          width={1}
                          height={30}
                          fontSize={8}
                        />
                      </div>
                      <button
                        onClick={() => printReturnLabel({
                          ...item,
                          receivedBy: auth.currentUser?.email,
                          orderId: orderData?.orderId || orderData?.id,
                          returnNote: returnNote
                        })}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold py-2 rounded-lg"
                      >
                        🖨️ Print
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={closeReturnRCVDialog}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-lg"
              >
                ✓ Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Return Orders View
  if (currentView === 'returns') {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <ToastNotification />
        
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Return Orders Management</h1>
                <p className="text-gray-600">রিটার্ন অর্ডার ম্যানেজমেন্ট</p>
              </div>
              <button
                onClick={() => {
                  setCurrentView('scanner');
                  setOrderData(null);
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg transition duration-200"
              >
                Back to Scanner
              </button>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">মোট রিটার্ন</h3>
              <p className="text-4xl font-bold text-blue-600">{returnStats.total}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">গ্রহণ করা হয়েছে</h3>
              <p className="text-4xl font-bold text-green-600">{returnStats.received}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">গ্রহণ বাকি</h3>
              <p className="text-4xl font-bold text-orange-600">{returnStats.pending}</p>
            </div>
          </div>

          {/* Return Orders Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">Return Orders List</h2>
                <button
                  onClick={fetchReturnOrders}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  Refresh
                </button>
              </div>
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <div className="text-xl font-semibold text-gray-700">Loading...</div>
              </div>
            ) : returnOrders.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-600 text-lg">No return orders found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Order ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Order Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Products</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Return Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">RCV By</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap sticky right-0 bg-gray-50">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {returnOrders.map((order) => {
                      const isReceived = order.CourierReturnRCV === true;
                      return (
                        <tr 
                          key={order.id}
                          className={`${!isReceived ? 'bg-orange-50' : ''} hover:bg-gray-50`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {order.customerInfo?.name || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {order.customerInfo?.phone || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 font-mono">
                              {order.orderId || order.id}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-gray-900">
                              {getTimeAgo(order.created_at)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatTimestamp(order.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {order.cartItems?.length || 0} items
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isReceived ? (
                              <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                ✓ Received
                              </span>
                            ) : (
                              <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                                ⏳ Pending
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {order.returnedRcvBy || '-'}
                            </div>
                            {order.returnRcvAt && (
                              <div className="text-xs text-gray-500">
                                {formatTimestamp(order.returnRcvAt)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm sticky right-0 bg-white">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentView('scanner');
                                fetchOrderDetails(order.id);
                              }}
                              className="text-white bg-blue-600 hover:bg-blue-700 font-semibold px-4 py-2 rounded-lg transition duration-200"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <ToastNotification />
      {showReturnRCVDialog && <ReturnRCVDialog />}
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Order Scanner</h1>
              <p className="text-gray-600">Scan QR code or enter Order ID to view order details</p>
            </div>
            <button
              onClick={() => setCurrentView('returns')}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-6 rounded-lg transition duration-200"
            >
              View Return Orders
            </button>
          </div>
        </div>

        {!orderData && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            {/* Manual Search */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Manual Search</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search by Order ID</label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={manualOrderId}
                      onChange={(e) => setManualOrderId(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleManualSearch()}
                      placeholder="Enter Order ID..."
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={handleManualSearch}
                      disabled={loading}
                      className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg transition duration-200 disabled:bg-gray-400"
                    >
                      Search
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search by Phone Number</label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={searchPhone}
                      onChange={(e) => setSearchPhone(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handlePhoneSearch()}
                      placeholder="Enter Phone Number..."
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <button
                      onClick={handlePhoneSearch}
                      disabled={loading}
                      className="bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-lg transition duration-200 disabled:bg-gray-400"
                    >
                      Search
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="text-gray-500 font-medium">OR</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>

            {/* QR Scanner */}
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Scan QR Code</h2>
              
              {!scanning ? (
                <button
                  onClick={handleStartScanning}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-4 rounded-lg transition duration-200 flex items-center justify-center gap-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                  Start QR Scanner
                </button>
              ) : (
                <div>
                  <div id="qr-reader" ref={scannerRef} className="mb-4"></div>
                  <button
                    onClick={handleStopScanning}
                    className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-lg transition duration-200"
                  >
                    Stop Scanner
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-6">
            <p className="font-medium">{error}</p>
          </div>
        )}

        {/* Loading */}
        {loading && !orderData && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-2xl font-semibold text-gray-700">Loading order details...</div>
          </div>
        )}

        {/* Order Details */}
        {orderData && !loading && (
          <div className="space-y-6">
            {/* Action Buttons */}
            <div className="flex justify-between items-center gap-3">
              <button
                onClick={handleReset}
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg transition duration-200"
              >
                Scan Another Order
              </button>

              {orderData.status === 'Return' && orderData.CourierReturnRCV !== true && (
                <button
                  onClick={startReturnRCVProcess}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-lg transition duration-200 flex items-center gap-2 text-lg"
                >
                  <span>📦</span>
                  <span>Process Return RCV (with Barcode)</span>
                </button>
              )}

              {orderData.status === 'Return' && orderData.CourierReturnRCV === true && (
                <div className="bg-green-100 border-2 border-green-500 text-green-800 font-semibold py-2 px-6 rounded-lg flex items-center gap-2">
                  <span>✓</span>
                  <span>Return Received</span>
                  {orderData.returnProcessedProducts && (
                    <span className="ml-2 text-sm">({orderData.returnProcessedProducts} products)</span>
                  )}
                </div>
              )}
            </div>

            {/* Basic Order Info */}
            <div className={`rounded-lg shadow-md p-6 ${orderData.status === 'Return' ? 'bg-orange-50 border-2 border-orange-300' : 'bg-white'}`}>
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-800">Order Information</h2>
                {orderData.status === 'Return' && (
                  <span className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                    RETURN ORDER
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Order ID</h3>
                  <p className="text-lg font-semibold text-gray-900">{orderData.orderId || orderData.id}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Consignment ID</h3>
                  <p className="text-lg font-semibold text-gray-900">{orderData.steadfastConsignmentId || 'N/A'}</p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Status</h3>
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(orderData.status)}`}>
                    {orderData.status || 'N/A'}
                  </span>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Order Date</h3>
                  <p className="text-lg font-semibold text-gray-900">{formatTimestamp(orderData.created_at)}</p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Total Amount</h3>
                  <p className="text-lg font-semibold text-gray-900">৳{orderData.totalPrice || orderData.totalAmount || 'N/A'}</p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Payment Method</h3>
                  <p className="text-lg font-semibold text-gray-900">{orderData.paymentMethod || 'N/A'}</p>
                </div>

                {orderData.status === 'Return' && orderData.CourierReturnRCV === true && (
                  <>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Received By</h3>
                      <p className="text-lg font-semibold text-green-700">{orderData.returnedRcvBy || 'N/A'}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Received At</h3>
                      <p className="text-lg font-semibold text-green-700">{formatTimestamp(orderData.returnRcvAt)}</p>
                    </div>
                    {orderData.returnRcvNote && (
                      <div className="md:col-span-2">
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Return Note</h3>
                        <p className="text-lg text-gray-900 bg-yellow-50 p-3 rounded border border-yellow-200">{orderData.returnRcvNote}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Customer Info */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Customer Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Name</h3>
                  <p className="text-lg text-gray-900">{orderData.customerInfo?.name || 'N/A'}</p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Phone</h3>
                  <p className="text-lg text-gray-900">{orderData.customerInfo?.phone || 'N/A'}</p>
                </div>

                <div className="md:col-span-2">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Address</h3>
                  <p className="text-lg text-gray-900">{orderData.customerInfo?.address || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Products */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Products</h2>
              
              <div className="space-y-3">
                {orderData.cartItems?.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/80';
                      }}
                    />
                    <div className="flex-1">
                      <p className="text-lg font-semibold text-gray-900">{item.name}</p>
                      <p className="text-sm text-gray-600">
                        Quantity: {item.quantity} × ৳{item.price}
                      </p>
                      {item.barcode && (
                        <p className="text-xs text-gray-500 font-mono mt-1">Barcode: {item.barcode}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">৳{item.quantity * item.price}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Call Notes */}
            {callNotes.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  Call Notes ({callNotes.length})
                </h2>
                
                <div className="space-y-4">
                  {callNotes.map((note, index) => (
                    <div key={note.id || index} className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded-r-lg">
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-lg font-semibold text-gray-900">{note.note}</p>
                        <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                          {formatTimestamp(note.timestamp)}
                        </span>
                      </div>
                      {note.email && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">By:</span> {note.email}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pathao Status History */}
            {pathaoStatus.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  Delivery Status History ({pathaoStatus.length})
                </h2>
                
                <div className="relative">
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-300"></div>
                  
                  <div className="space-y-6">
                    {pathaoStatus.map((statusItem, index) => (
                      <div key={statusItem.id || index} className="relative pl-14">
                        <div className="absolute left-4 top-2 w-4 h-4 bg-blue-500 rounded-full border-4 border-white"></div>
                        
                        <div className={`p-4 rounded-lg border-2 ${getStatusColor(statusItem.status)}`}>
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="text-lg font-bold">{statusItem.status}</h3>
                            <span className="text-sm font-medium whitespace-nowrap ml-4">
                              {formatTimestamp(statusItem.updated_at)}
                            </span>
                          </div>
                          
                          {statusItem.remarks && (
                            <p className="text-sm mt-2">
                              <span className="font-medium">Remarks:</span> {statusItem.remarks}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {callNotes.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Call Notes</h2>
                <p className="text-gray-500 text-center py-8">No call notes available for this order</p>
              </div>
            )}

            {pathaoStatus.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Delivery Status History</h2>
                <p className="text-gray-500 text-center py-8">No status history available for this order</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add CSS for toast animation */}
      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default OrderScanner;